import { Link } from "react-router-dom";
import type { Profile } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";

interface StatusSectionsProps {
  active: "vendedor" | "membresia" | "notificaciones";
  profile: Profile;
}

const SELLER_STATUS: Record<string, { label: string; icon: string; tone: string }> = {
  none: {
    label: "Todavía no sos vendedor",
    icon: "ri-store-2-line",
    tone: "text-foreground-500",
  },
  pending: {
    label: "Solicitud enviada",
    icon: "ri-time-line",
    tone: "text-secondary-700",
  },
  approved: {
    label: "Vendedor aprobado",
    icon: "ri-checkbox-circle-line",
    tone: "text-primary-700",
  },
  rejected: {
    label: "Solicitud rechazada",
    icon: "ri-close-circle-line",
    tone: "text-accent-700",
  },
  suspended: {
    label: "Cuenta suspendida",
    icon: "ri-pause-circle-line",
    tone: "text-accent-700",
  },
};

const MEMBERSHIP_STATUS: Record<string, { label: string; tone: string }> = {
  none: { label: "Sin membresía", tone: "text-foreground-500" },
  active: { label: "Membresía activa", tone: "text-primary-700" },
  expired: { label: "Membresía expirada", tone: "text-accent-700" },
  cancelled: { label: "Membresía cancelada", tone: "text-foreground-500" },
  suspended: { label: "Membresía suspendida", tone: "text-accent-700" },
};

export default function StatusSections({ active, profile }: StatusSectionsProps) {
  const { showToast } = useToast();

  if (active === "vendedor") {
    const status = SELLER_STATUS[profile.seller_status] ?? SELLER_STATUS.none;
    return (
      <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          Configuración de vendedor
        </h2>
        <p className="mt-1 text-sm text-foreground-600">
          Gestioná tu cuenta de vendedor y el estado de tu solicitud.
        </p>

        <div className="mt-5 flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100">
            <i className={`${status.icon} text-xl ${status.tone}`}></i>
          </span>
          <div>
            <p className={`text-sm font-semibold ${status.tone}`}>
              {status.label}
            </p>
            {profile.seller_verified && (
              <p className="mt-0.5 text-xs text-foreground-500">
                Cuenta aprobada por VendeYa.
              </p>
            )}
          </div>
        </div>

        {profile.seller_status === "none" && (
          <Link
            to="/perfil/solicitud-vendedor"
            className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-store-2-line"></i>
            Registrarme como vendedor
          </Link>
        )}

        {profile.seller_status === "rejected" && (
          <Link
            to="/perfil/solicitud-vendedor"
            className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line"></i>
            Volver a solicitar
          </Link>
        )}

        {profile.seller_status === "approved" && (
          <Link
            to="/seller/dashboard"
            className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-dashboard-line"></i>
            Ir a mi panel de vendedor
          </Link>
        )}
      </section>
    );
  }

  if (active === "membresia") {
    const status =
      MEMBERSHIP_STATUS[profile.membership_status] ?? MEMBERSHIP_STATUS.none;
    return (
      <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          Membresía VendeYa Verificado
        </h2>
        <p className="mt-1 text-sm text-foreground-600">
          La insignia azul de verificación, independiente de tu aprobación como
          vendedor.
        </p>

        <div className="mt-5 flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 p-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100">
            <i
              className={`${
                profile.verified_status === "verified"
                  ? "ri-verified-badge-fill text-primary-600"
                  : "ri-verified-badge-line text-foreground-400"
              } text-xl`}
            ></i>
          </span>
          <div>
            <p className={`text-sm font-semibold ${status.tone}`}>
              {status.label}
            </p>
            {profile.membership_plan && (
              <p className="mt-0.5 text-xs text-foreground-500">
                Plan: {profile.membership_plan}
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            showToast(
              "La membresía VendeYa Verificado estará disponible muy pronto.",
              "info"
            )
          }
          className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          <i className="ri-verified-badge-line"></i>
          Conocer VendeYa Verificado
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
      <h2 className="font-heading text-lg font-bold text-foreground-950">
        Notificaciones
      </h2>
      <p className="mt-1 text-sm text-foreground-600">
        Elegí qué correos querés recibir de VendeYa.
      </p>
      <Link
        to="/preferencias-email"
        className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
      >
        <i className="ri-mail-settings-line"></i>
        Configurar preferencias de email
      </Link>
    </section>
  );
}